import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsCredits, setNeedsCredits] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data.user);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    setUser(userData);
    return response.data;
  };

  const signup = async (email, password, name, captchaId, captchaText, promoCode = '') => {
    const response = await api.post('/auth/signup', { email, password, name, captchaId, captchaText, promoCode });
    const { token, user: userData, needsCredits: credits } = response.data;
    localStorage.setItem('token', token);
    setUser(userData);
    setNeedsCredits(credits);
    return response.data;
  };

  const unlockSenderId = () => {
    if (user) {
      setUser({ ...user, senderIdUnlocked: true });
    }
  };

  const setSenderIdUnlocked = (unlocked) => {
    if (user) {
      setUser({ ...user, senderIdUnlocked: !!unlocked });
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setNeedsCredits(false);
  };

  const updateCredits = (newBalance) => {
    if (user) {
      setUser({ ...user, credits: newBalance });
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value = {
    user,
    loading,
    needsCredits,
    setNeedsCredits,
    login,
    signup,
    logout,
    updateCredits,
    refreshUser,
    unlockSenderId,
    setSenderIdUnlocked,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
